import { Badge } from '@/components/ui/badge'

const statusOptions = [
  { value: 1, label: 'Ativo' },
  { value: 0, label: 'Inativo' },
]

function statusBadge(item) {
  return Number(item.status) === 1
    ? <Badge variant="success">Ativo</Badge>
    : <Badge variant="muted">Inativo</Badge>
}

export const configs = {
  clientes: {
    title: 'Cliente',
    titlePlural: 'Clientes',
    description: 'Tomadores dos serviços e destinatários das notas fiscais.',
    endpoint: '/clientes',
    idKey: 'clienteID',
    searchPlaceholder: 'Buscar por nome, CNPJ ou e-mail...',
    modalSize: 'lg',
    columns: [
      { key: 'nome', label: 'Razão Social' },
      { key: 'cnpj', label: 'CNPJ' },
      { key: 'email', label: 'E-mail', render: (i) => i.email || '—' },
      { key: 'municipio', label: 'Município', render: (i) => `${i.municipioNome}/${i.estadoUf}` },
      { key: 'status', label: 'Status', render: statusBadge, className: 'w-28' },
    ],
    fields: [
      { name: 'nome', label: 'Razão Social', required: true, colSpan: 2 },
      { name: 'cnpj', label: 'CNPJ', required: true, mask: 'cnpj', placeholder: '00.000.000/0000-00' },
      { name: 'email', label: 'E-mail', type: 'email' },
      {
        name: 'municipioID', label: 'Município', type: 'asyncSelect', endpoint: '/municipios',
        optionValue: 'municipioID', optionLabel: 'nome', required: true,
        formatLabel: (m) => `${m.nome}/${m.estadoUf}`,
        initialLabel: (i) => (i.municipioNome ? `${i.municipioNome}/${i.estadoUf}` : ''),
      },
      { name: 'cep', label: 'CEP', mask: 'cep', placeholder: '00.000-000' },
      { name: 'numero', label: 'Número' },
      { name: 'rua', label: 'Rua', colSpan: 2 },
      { name: 'bairro', label: 'Bairro' },
      { name: 'complemento', label: 'Complemento' },
      { name: 'obs', label: 'Observação', type: 'textarea', colSpan: 2 },
      { name: 'status', label: 'Status', type: 'select', options: statusOptions, default: 1 },
    ],
  },

  servicos: {
    title: 'Serviço',
    titlePlural: 'Serviços',
    description: 'Serviços disponíveis para vincular aos contratos.',
    endpoint: '/servicos',
    idKey: 'servicoID',
    searchPlaceholder: 'Buscar serviço...',
    columns: [
      { key: 'nome', label: 'Nome' },
      { key: 'tipo', label: 'Tipo', className: 'w-40' },
      { key: 'status', label: 'Status', render: statusBadge, className: 'w-28' },
    ],
    fields: [
      { name: 'nome', label: 'Nome', required: true, colSpan: 2 },
      { name: 'tipo', label: 'Tipo', type: 'select', required: true, options: [
        { value: 'recorrente', label: 'Recorrente' },
        { value: 'nao recorrente', label: 'Não recorrente' },
      ] },
      { name: 'status', label: 'Status', type: 'select', options: statusOptions, default: 1 },
      { name: 'descricao', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
  },

  modalidades: {
    title: 'Modalidade',
    titlePlural: 'Modalidades',
    description: 'Modalidades de compra utilizadas nos processos.',
    endpoint: '/modalidades',
    idKey: 'modalidadeID',
    columns: [{ key: 'nome', label: 'Nome' }],
    fields: [{ name: 'nome', label: 'Nome', required: true, colSpan: 2 }],
  },

  'indices-reajuste': {
    title: 'Índice de Reajuste',
    titlePlural: 'Índices de Reajuste',
    description: 'Índices aplicados no reajuste dos contratos (IPCA, INPC, etc.).',
    endpoint: '/indices-reajuste',
    idKey: 'indiceReajusteID',
    columns: [{ key: 'nome', label: 'Nome' }],
    fields: [{ name: 'nome', label: 'Nome', required: true, colSpan: 2 }],
  },

  tributacoes: {
    title: 'Tributação Nacional',
    titlePlural: 'Tributações Nacionais',
    description: 'Códigos de serviço da LC 116/2003 usados na emissão da NFS-e.',
    endpoint: '/tributacoes',
    idKey: 'tributacaoNacionalID',
    searchPlaceholder: 'Buscar por código ou nome...',
    columns: [
      { key: 'codigo', label: 'Código', className: 'w-32' },
      { key: 'nome', label: 'Descrição' },
    ],
    fields: [
      { name: 'codigo', label: 'Código', required: true },
      { name: 'nome', label: 'Descrição', required: true, colSpan: 2 },
    ],
  },

  estados: {
    title: 'Estado',
    titlePlural: 'Estados',
    description: 'Unidades federativas.',
    endpoint: '/estados',
    idKey: 'estadoID',
    columns: [
      { key: 'nome', label: 'Nome' },
      { key: 'uf', label: 'UF', className: 'w-20' },
    ],
    fields: [
      { name: 'nome', label: 'Nome', required: true, colSpan: 2 },
      { name: 'uf', label: 'UF', required: true },
    ],
  },

  municipios: {
    title: 'Município',
    titlePlural: 'Municípios',
    description: 'Municípios e respectivos códigos IBGE.',
    endpoint: '/municipios',
    idKey: 'municipioID',
    searchPlaceholder: 'Buscar por nome ou IBGE...',
    columns: [
      { key: 'nome', label: 'Nome' },
      { key: 'estadoUf', label: 'UF', className: 'w-20' },
      { key: 'ibge', label: 'IBGE', className: 'w-32' },
    ],
    fields: [
      { name: 'nome', label: 'Nome', required: true, colSpan: 2 },
      { name: 'estadoID', label: 'Estado', type: 'asyncSelect', endpoint: '/estados', optionValue: 'estadoID', optionLabel: 'nome', labelKey: 'estadoNome', required: true },
      { name: 'ibge', label: 'Código IBGE', required: true },
      { name: 'populacao', label: 'População', type: 'number' },
      { name: 'cep', label: 'CEP' },
    ],
  },

  usuarios: {
    title: 'Usuário',
    titlePlural: 'Usuários',
    description: 'Usuários com acesso ao sistema.',
    endpoint: '/usuarios',
    idKey: 'usuarioID',
    searchPlaceholder: 'Buscar por nome, e-mail ou CPF...',
    columns: [
      { key: 'nome', label: 'Nome' },
      { key: 'email', label: 'E-mail' },
      { key: 'cpf', label: 'CPF', className: 'w-40' },
      { key: 'status', label: 'Status', render: statusBadge, className: 'w-28' },
    ],
    fields: [
      { name: 'nome', label: 'Nome', required: true, colSpan: 2 },
      { name: 'email', label: 'E-mail', required: true },
      { name: 'cpf', label: 'CPF', required: true },
      { name: 'senha', label: 'Senha', type: 'password', hint: 'Na edição, deixe em branco para manter a senha atual.' },
      { name: 'status', label: 'Status', type: 'select', options: statusOptions, default: 1 },
    ],
  },
}
